import { Card } from '@weareberyl/design-system'
import { ColumnProps } from 'antd/lib/table'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import { useMemo } from 'react'
import { withScheme } from '~/components/Scheme'
import { WithScheme } from '~/components/Scheme/withScheme'
import QueryTable from '~/components/Table/QueryTable'
import { useTableQuery } from '~/components/Table/useTableQuery'
import TabTitle from '~/components/shared/TabTitle'
import TableIdCopy from '~/components/shared/TableIdCopy'
import Tabs from '~/components/shared/Tabs'
import * as routes from '~/constants/routes'
import {
  Direction,
  InvoiceListQuery_table_IInvoiceConnection_nodes as Invoice,
  InvoiceListQueryVariables,
  useInvoiceListQuery,
} from '~/gql/generated/graphql'
import { formatDatetime } from '~/utils'
import { isPhone } from '~/utils/mobileDetect'
import { useQueryParams } from '~/utils/useQueryParams'
import { InvoiceStatus } from '../../../__generated__/globalTypes'
import InvoiceStatusLabel from '../InvoiceStatus'
import { formatInvoiceType } from '../formatters'

const { Tab } = Tabs

const columns: ColumnProps<Invoice>[] = [
  {
    title: 'ID',
    dataIndex: 'id',
    render: (id: string) => <TableIdCopy id={id} />,
  },
  {
    title: 'User',
    dataIndex: ['user', 'email'],
  },
  {
    title: 'Status',
    dataIndex: 'status',
    render: status => <InvoiceStatusLabel status={status} />,
  },
  {
    title: 'Date/Time',
    dataIndex: 'created_at',
    render: t => formatDatetime(t),
  },
  {
    title: 'Description',
    dataIndex: 'description',
    className: 'xs-hide',
  },
  {
    title: 'Total Currency',
    dataIndex: ['currency_debit', 'formatted_amount'],
  },
  {
    title: 'Total Minutes',
    dataIndex: ['minute_debit', 'formatted_amount'],
  },
  {
    title: 'Type',
    dataIndex: '__typename',
    render: type => formatInvoiceType(type),
  },
]

const status = {
  all: 'all',
  refunded: 'refunded',
  ...InvoiceStatus,
}

export type InvoiceListProps = {
  user_id?: string
}
type Props = WithScheme & RouteComponentProps & InvoiceListProps
const InvoiceList = ({ scheme, history, user_id }: Props) => {
  const [{ current, pageSize, currentTab }, setQueryParams] = useQueryParams(
    'invoice-table',
    {
      currentTab: status.all,
    },
  )

  const getTableBorderProps = () => {
    switch (currentTab) {
      case status.all:
        return { borderTopLeftRadius: 0 }
      case status.void:
        return { borderTopRightRadius: 0 }
      default:
        return {}
    }
  }

  const queryVars = useMemo(() => {
    const queryVars: InvoiceListQueryVariables = {
      scheme_id: scheme.id,
      user_id,
      order_by: [{ id: Direction.desc }],
      is_mobile: isPhone(),
      paginate: {
        page: current,
        per_page: pageSize,
      },
      where: [] as object[],
    }

    if (currentTab !== status.all) {
      if (currentTab === status.refunded) {
        queryVars.where = [
          {
            status: {
              in: ['fully_refunded', 'partially_refunded'],
            },
          },
        ]
      } else {
        queryVars.where = [{ status: { eq: currentTab } }]
      }
    }

    return queryVars
  }, [scheme, user_id, current, pageSize, currentTab])

  const { data, loading } = useTableQuery(useInvoiceListQuery, {
    pollInterval: 0,
    variables: queryVars,
  })

  return (
    <Card variant="gray" p={3}>
      <Tabs>
        <Tab
          onPress={() => setQueryParams({ currentTab: status.all, current: 1 })}
          isSelected={currentTab === status.all}
        >
          <TabTitle tabText="All" icon="burger" />
        </Tab>
        <Tab
          onPress={() =>
            setQueryParams({ currentTab: status.outstanding, current: 1 })
          }
          isSelected={currentTab === status.outstanding}
        >
          <TabTitle tabText="Outstanding" icon="warning" />
        </Tab>
        <Tab
          onPress={() =>
            setQueryParams({ currentTab: status.settled, current: 1 })
          }
          isSelected={currentTab === status.settled}
        >
          <TabTitle tabText="Settled" icon="tick" />
        </Tab>
        <Tab
          onPress={() =>
            setQueryParams({ currentTab: status.refunded, current: 1 })
          }
          isSelected={currentTab === status.refunded}
        >
          <TabTitle tabText="Refunded" icon="repeat" />
        </Tab>
        <Tab
          onPress={() =>
            setQueryParams({ currentTab: status.void, current: 1 })
          }
          isSelected={currentTab === status.void}
        >
          <TabTitle tabText="Void" icon="cross" />
        </Tab>
      </Tabs>
      <Card variant="borderless" {...getTableBorderProps()}>
        <QueryTable
          id="invoices-table"
          columns={columns}
          onChange={({ current, pageSize }) =>
            setQueryParams({ current, pageSize })
          }
          onRow={record => {
            return {
              onClick: () => {
                const invoicePath = routes.prependScheme(
                  `${routes.INVOICES}/${record.id}`,
                  scheme.id,
                )
                history.push(invoicePath)
              },
            }
          }}
          loading={loading}
          data={data}
        />
      </Card>
    </Card>
  )
}

export default withRouter(withScheme(InvoiceList))
